/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

/* eslint-disable no-underscore-dangle */
import * as Sentry from '@sentry/react';
import { Layout, message } from 'antd';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import {
  Department,
  Organization,
  OrganizationBillingStatus,
  AccessControlRole,
  User,
  useGetUserLazyQuery
} from '@/generated/graphql';

// Local Includes
import DepartmentNotFound from '@/components/DepartmentNotFound';
import Loader, { ILoaderType } from '@/components/Loader';
import PermissionError from '@/components/PermissionError';
import WithSuspense from '@/components/WithSuspense';
import { currentUserDetailsVar } from '@/graphql/cache/user';
import CurrentDrawer from '@/popups/CurrentDrawer';
import { useApp } from '@/provider/app';
import AnalyticsTracker, { AnalyticsGlobalProperties } from '@/tracker';
import { permissionUtil } from '@/utils/permissionUtil';
import AppLeftNav, { COLLAPSED_WIDTH } from '@/views/AppLeftNav';
import AppTopHeader, { HEADER_HEIGHT } from '@/views/AppTopHeader';
import CurrentPopup from '@popups/CurrentPopup';
import { addUserToIntercom } from '@/components/IntercomWidget/helper';
import SentryErrorPage from './SentryErrorPage';

import styles from './AppPage.module.less';

const { Content } = Layout;

/**
 * This is layout page. `<Outlet/>` is the placeholder for dynamic components
 * @returns
 */
function AppPage() {
  const [loginUserError, setLoginUserError] = useState<Error | null>(null);
  const {
    currentDepartment: department,
    loading: departmentLoading,
    error,
    username,
    organization
  } = useApp();
  const [getLoggedInUser] = useGetUserLazyQuery({
    onError(error) {
      setLoginUserError(error);
      console.error(error.message);
      message.error('Failed to fetch user details');
      Sentry.captureException(error);
    }
  });

  const addUserDataToCache = useCallback(
    (latestDepartment: Department, user: User, org: Organization) => {
      currentUserDetailsVar({
        department: latestDepartment,
        permissions: permissionUtil.getOrgPermissions(org),
        email: username,
        name: user?.firstName || username?.split('@')[0],
        lastName: user?.lastName,
        id: username, // TODO: Add a proper id
        role: user?.role as AccessControlRole,
        organization: org,
        isDemoUser:
          org &&
          org?.billingStatus === OrganizationBillingStatus.Unpaid.valueOf(),
        dataLoading: false
      });

      // Add user data to window object for Whatfix to use
      if (isEmpty((window as any)._kog_settings)) {
        (window as any)._kog_settings = {};
      }
      (window as any)._kog_settings.user = username;
      (window as any)._kog_settings.role = user.role;

      const analyticsData: AnalyticsGlobalProperties = {
        department: latestDepartment.name,
        role: user.role?.valueOf(),
        isDemoUser:
          org &&
          org?.billingStatus === OrganizationBillingStatus.Unpaid.valueOf(),
        accountType:
          org?.billingStatus === OrganizationBillingStatus.Unpaid.valueOf()
            ? 'Unpaid'
            : 'Paid'
      };
      if (org && org?.name) {
        analyticsData.organization = org?.name;
      }
      AnalyticsTracker.setGlobalProperties(analyticsData);
      addUserToIntercom(user);
    },
    [username]
  );

  useEffect(() => {
    if (!departmentLoading && department) {
      getLoggedInUser({
        variables: {
          departmentId: department.id
        },
        onCompleted: (data) => {
          const user = data?.getUser;
          if (user) {
            addUserDataToCache(department, user, organization!);
          }
        }
      });
    } else if (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Failed to fetch department details'
        }
      });
      message.error(error.message || 'Failed to fetch department');
    }
  }, [
    departmentLoading,
    department,
    getLoggedInUser,
    addUserDataToCache,
    organization,
    error
  ]);

  // ToDo: Improve on this routing logic.
  const renderContent = () => {
    if (!departmentLoading && !department) {
      return <DepartmentNotFound />;
    }

    if (loginUserError) {
      return <PermissionError />;
    }

    if (department && !departmentLoading) {
      return <Outlet />;
    }

    return (
      <div>
        <Loader type={ILoaderType.FULL_PAGE} message="Loading application.." />
      </div>
    );
  };

  return (
    <Layout>
      <AppTopHeader />
      <AppLeftNav top={HEADER_HEIGHT} />
      <Content
        style={{ marginLeft: COLLAPSED_WIDTH, marginTop: HEADER_HEIGHT }}
      >
        <Layout className={styles.mainLayout}>
          <div className={styles.pageContent}>
            <Sentry.ErrorBoundary
              fallback={SentryErrorPage}
              showDialog
              onError={(error) => {
                Sentry.captureException(error);
              }}
            >
              <WithSuspense element={<>{renderContent()}</>} />
            </Sentry.ErrorBoundary>
          </div>
        </Layout>
      </Content>
      <CurrentPopup />
      <CurrentDrawer />
    </Layout>
  );
}

export default AppPage;
